import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { ROLE_LABEL } from "@/constants/roles";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import EmployeeAvatar from "@/components/communications/EmployeeAvatar";
import { useSignOut } from "@/components/layout/SignOutDialog";
import { notificationCategory } from "@/pages/NotificationCenter";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Search,
  Bell,
  Plus,
  Sun,
  Moon,
  LogOut,
  Globe,
  Command,
  Menu,
} from "lucide-react";

const REGIONS = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "GCC", label: "UAE / GCC" },
  { code: "IN", label: "India" },
];

export default function Topbar({ onOpenPalette, onOpenMobileNav, title }) {
  const { user, can } = useAuth();
  const { signOut, dialog: signOutDialog } = useSignOut();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [region, setRegion] = useState(
    () => localStorage.getItem("nexreq-region") || user?.region || "US",
  );
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [profilePhotoId, setProfilePhotoId] = useState(null);
  const [notificationFilter, setNotificationFilter] = useState("all");
  const filteredNotifications = notifs.items.filter((notification) => {
    if (notificationFilter === "all") return true;
    return notificationCategory(notification) === notificationFilter;
  });

  useEffect(() => {
    localStorage.setItem("nexreq-region", region);
  }, [region]);
  useEffect(() => {
    const loadNotifs = () =>
      api.get("/meta/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
    loadNotifs();
    const timer = setInterval(loadNotifs, 15000);
    return () => clearInterval(timer);
  }, []);
  const markAllRead = async () => {
    await api.post("/notify/items/read-all").catch(() => {});
    setNotifs((current) => ({ unread: 0, items: current.items.map((item) => ({ ...item, read: true })) }));
  };
  useEffect(() => {
    api
      .get("/hrms/me/profile-photo")
      .then(({ data }) => setProfilePhotoId(data.profile_photo_id || null))
      .catch(() => setProfilePhotoId(null));
  }, []);

  const openNotification = async (notification) => {
    try {
      await api.post(`/meta/notifications/${notification.id}/read`);
      setNotifs((current) => ({
        ...current,
        unread: Math.max(0, current.unread - (notification.read ? 0 : 1)),
        items: current.items.map((item) =>
          item.id === notification.id ? { ...item, read: true } : item,
        ),
      }));
      navigate(notification.destination || "/");
    } catch {
      navigate(notification.destination || "/");
    }
  };

  return (
    <header
      className={
        "h-16 sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6 border-b " +
        "border-border backdrop-blur-md bg-background/80"
      }
    >
      <Button
        data-testid="mobile-menu-btn"
        variant="outline"
        size="icon"
        className="md:hidden"
        onClick={onOpenMobileNav}
      >
        <Menu className="h-4 w-4" />
      </Button>
      <div className="flex-1 min-w-0">
        <h1
          data-testid="page-title"
          className="font-display font-semibold text-lg tracking-tight truncate"
        >
          {title}
        </h1>
      </div>

      <button
        data-testid="global-search-trigger"
        onClick={onOpenPalette}
        className={
          "hidden md:flex items-center gap-2 h-9 px-3 rounded-lg border border-border " +
          "bg-card text-sm text-muted-foreground hover:border-[#255058]/40 " +
          "transition-colors w-64"
        }
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search…</span>
        <kbd
          className={
            "text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted flex " +
            "items-center gap-0.5"
          }
        >
          <Command className="h-2.5 w-2.5" />K
        </kbd>
      </button>

      <Button
        data-testid="global-search-mobile"
        variant="outline"
        size="icon"
        className="md:hidden"
        onClick={onOpenPalette}
      >
        <Search className="h-4 w-4" />
      </Button>

      {/* Region selector */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="region-selector"
            variant="outline"
            size="sm"
            className="gap-1.5 hidden sm:flex"
          >
            <Globe className="h-4 w-4" /> {region}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Business Region</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {REGIONS.map((r) => (
            <DropdownMenuItem
              key={r.code}
              data-testid={`region-option-${r.code}`}
              onClick={() => setRegion(r.code)}
            >
              {r.label}{" "}
              {region === r.code && (
                <span className="ml-auto text-[#255058]">●</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Quick create */}
      {can("candidate:create") && user?.role !== "founder" && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              data-testid="quick-create-button"
              size="sm"
              className="gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white"
            >
              <Plus className="h-4 w-4" />{" "}
              <span className="hidden sm:inline">Create</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              data-testid="quick-create-candidate"
              onClick={() => navigate("/candidates?new=1")}
            >
              New Candidate
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Notifications */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            data-testid="notification-center-trigger"
            variant="ghost"
            size="icon"
            className="relative"
          >
            <Bell className="h-5 w-5" />
            {notifs.unread > 0 && (
              <span
                className={
                  "absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full " +
                  "bg-[#255058] text-white text-[9px] grid place-items-center"
                }
              >
                {notifs.unread}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border font-medium text-sm">
            Notifications
            <button type="button" onClick={markAllRead} className="text-xs text-[#2f8f86] hover:underline" data-testid="notification-mark-all-read">
              Mark all read
            </button>
          </div>
          <div className="flex flex-wrap gap-1 border-b border-border px-3 py-2">
            {["all", "requests", "attendance", "announcements", "tasks"].map((filter) => (
              <Button
                key={filter}
                size="sm"
                variant={notificationFilter === filter ? "default" : "ghost"}
                onClick={() => setNotificationFilter(filter)}
                data-testid={`notification-filter-${filter}`}
                className={`h-7 px-2 text-xs capitalize ${notificationFilter === filter ? "bg-[#255058] hover:bg-[#1c3d43]" : ""}`}
              >
                {filter}
              </Button>
            ))}
          </div>
          <div className="max-h-80 overflow-y-auto scrollbar-thin">
            {filteredNotifications.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                You're all caught up.
              </div>
            )}
            {filteredNotifications.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => openNotification(n)}
                data-testid={`notification-item-${n.id}`}
                className={
                  "w-full border-b border-border/60 px-4 py-3 text-left hover:bg-accent/50 " +
                  "transition-colors " +
                  (n.read ? "" : "bg-[#255058]/5")
                }
              >
                <div className="text-sm font-medium">{n.title}</div>
                {n.body && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {n.body}
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {timeAgo(n.created_at)}
                </div>
              </button>
            ))}
          </div>
          <button type="button" onClick={() => navigate("/notifications")} className="w-full px-4 py-2 text-center text-xs font-medium text-[#2f8f86] hover:bg-accent/50" data-testid="notification-view-all">
            View all notifications & settings
          </button>
        </PopoverContent>
      </Popover>

      <Button
        data-testid="theme-toggle"
        variant="ghost"
        size="icon"
        onClick={toggle}
      >
        {theme === "dark" ? (
          <Sun className="h-5 w-5" />
        ) : (
          <Moon className="h-5 w-5" />
        )}
      </Button>

      {/* Profile */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-profile-menu"
            className="flex items-center gap-2 pl-1"
          >
            <EmployeeAvatar
              className="h-8 w-8"
              fileId={profilePhotoId}
              name={user?.name}
              testId="topbar-profile-avatar"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <div className="px-2 py-1.5">
            <div className="font-medium text-sm truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">
              {user?.email}
            </div>
            <Badge variant="secondary" className="mt-1.5 text-[10px]">
              {ROLE_LABEL[user?.role] || user?.role}
            </Badge>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="logout-button"
            onClick={signOut}
            className="text-red-600"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out of app
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {signOutDialog}
    </header>
  );
}
